import { supabase } from '@/integrations/supabase/client';
import type { Checklist, ChecklistWithRelations, CreateChecklistInput } from './types';

type ChecklistRow = Checklist & {
  area?: { id: string; name: string; color?: string | null } | null;
  line?: { id: string; name: string } | null;
  station?: { id: string; name: string } | null;
  checklist_items?: { id: string }[];
  instruction_competencies?: ChecklistWithRelations['instruction_competencies'];
};

function mapChecklistRow(row: ChecklistRow): Checklist {
  return {
    id: row.id,
    tenant_id: row.tenant_id,
    factory_id: row.factory_id,
    name: row.name,
    description: row.description,
    checklist_kind: row.checklist_kind,
    is_active: row.is_active,
    area_id: row.area_id,
    line_id: row.line_id,
    station_id: row.station_id,
    created_by: row.created_by,
    created_at: row.created_at,
    updated_at: row.updated_at,
    name_translations: row.name_translations,
    description_translations: row.description_translations,
  };
}

function mapChecklistWithRelations(row: ChecklistRow): ChecklistWithRelations {
  return {
    ...mapChecklistRow(row),
    area: row.area ?? null,
    line: row.line ?? null,
    station: row.station ?? null,
    checklist_items: row.checklist_items ?? [],
    instruction_competencies: (row.instruction_competencies ?? []).filter(
      (item): item is NonNullable<typeof item> => Boolean(item?.competency),
    ),
  };
}

export async function fetchChecklistsByFactoryId(
  tenantId: string,
  factoryId: string,
): Promise<ChecklistWithRelations[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_templates')
    .select(
      `
      *,
      area:factory_areas(id, name, color),
      line:production_lines(id, name),
      station:stations(id, name),
      checklist_items:mod_checklist_template_items(id),
      instruction_competencies:mod_checklist_template_competencies(
        competency:competencies(id, name, description, color, is_global)
      )
    `,
    )
    .eq('tenant_id', tenantId)
    .eq('factory_id', factoryId)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return ((data ?? []) as ChecklistRow[]).map(mapChecklistWithRelations);
}

export async function fetchChecklistById(
  tenantId: string,
  checklistId: string,
): Promise<ChecklistWithRelations | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_templates')
    .select(
      `
      *,
      area:factory_areas(id, name, color),
      line:production_lines(id, name),
      station:stations(id, name),
      instruction_competencies:mod_checklist_template_competencies(
        competency:competencies(id, name, description, color, is_global)
      )
    `,
    )
    .eq('tenant_id', tenantId)
    .eq('id', checklistId)
    .single();

  if (error) throw error;
  return data ? mapChecklistWithRelations(data as ChecklistRow) : null;
}

export async function fetchActiveChecklistsForOperator(
  tenantId: string,
  factoryId: string,
): Promise<ChecklistWithRelations[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_templates')
    .select(
      `
      *,
      area:factory_areas(id, name, color),
      line:production_lines(id, name),
      station:stations(id, name),
      checklist_items:mod_checklist_template_items(*)
    `,
    )
    .eq('tenant_id', tenantId)
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  return ((data ?? []) as ChecklistRow[]).map(mapChecklistWithRelations);
}

export async function createChecklist(input: CreateChecklistInput): Promise<Checklist> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_templates')
    .insert({
      tenant_id: input.tenant_id,
      factory_id: input.factory_id,
      name: input.name,
      description: input.description ?? null,
      checklist_kind: input.checklist_kind ?? 'startup',
      is_active: input.is_active ?? true,
      area_id: input.area_id ?? null,
      line_id: input.line_id ?? null,
      station_id: input.station_id ?? null,
      created_by: input.created_by ?? null,
      name_translations: input.name_translations ?? {},
      description_translations: input.description_translations ?? {},
    })
    .select('*')
    .single();

  if (error) throw error;
  return mapChecklistRow(data as ChecklistRow);
}

export async function updateChecklist(
  tenantId: string,
  checklistId: string,
  updates: Partial<Omit<Checklist, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>>,
): Promise<void> {
  const payload: Record<string, unknown> = {
    name: updates.name,
    description: updates.description,
    checklist_kind: updates.checklist_kind,
    is_active: updates.is_active,
    area_id: updates.area_id,
    line_id: updates.line_id,
    station_id: updates.station_id,
    created_by: updates.created_by,
    name_translations: updates.name_translations,
    description_translations: updates.description_translations,
  };

  const sanitizedPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  );

  if (Object.keys(sanitizedPayload).length === 0) return;

  const { error } = await supabase
    .schema('core')
    .from('mod_checklist_templates')
    .update(sanitizedPayload)
    .eq('tenant_id', tenantId)
    .eq('id', checklistId);

  if (error) throw error;
}

export async function deleteChecklist(
  tenantId: string,
  checklistId: string,
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('mod_checklist_templates')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('id', checklistId);

  if (error) throw error;
}

export async function replaceInstructionCompetencies(
  tenantId: string,
  instructionId: string,
  competencyIds: string[],
): Promise<void> {
  const { error: deleteError } = await supabase
    .schema('core')
    .from('mod_checklist_template_competencies')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('template_id', instructionId);

  if (deleteError) throw deleteError;

  if (competencyIds.length === 0) return;

  const { error: insertError } = await supabase
    .schema('core')
    .from('mod_checklist_template_competencies')
    .insert(
      competencyIds.map((competencyId) => ({
        tenant_id: tenantId,
        template_id: instructionId,
        competency_id: competencyId,
      })),
    );

  if (insertError) throw insertError;
}
